import React, { useContext, useEffect, useRef, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Link } from "react-router-dom";
import moment from "moment/moment";
import { FaWandMagicSparkles } from "react-icons/fa6";
import { FiCalendar, FiTrendingUp, FiZap } from "react-icons/fi";
import { HiOutlineArrowsRightLeft } from "react-icons/hi2";
import { SelectionContext } from "../../Context/Store";
import {
  getBillingPlans,
  getBillingTransactions,
  getTopupPacks,
} from "../../api/context/billing";
import { buyTopupPack, subscribe } from "./checkout";
import CancelSubscriptionModal from "./components/CancelSubscriptionModal";
import TransactionTable from "./components/TransactionTable";
import PlanCard from "./components/PlanCard";
import TopupCard from "./components/TopupCard";
import PlanSwitchConfirmModal from "./components/PlanSwitchConfirmModal";
import TrialClaimBanner from "./components/TrialClaimBanner";

const DashboardView = ({ balance }) => {
  const { billingBalance, refreshBillingBalance } = useContext(SelectionContext);
  const activeBalance = balance || billingBalance;

  const [showCancel, setShowCancel] = useState(false);
  const [cancelMode, setCancelMode] = useState("current");
  const [confirmPlan, setConfirmPlan] = useState(null);
  const plansRef = useRef(null);
  const topupRef = useRef(null);

  const scrollTo = (ref) => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };
  const [recent, setRecent] = useState([]);
  const [recentLoading, setRecentLoading] = useState(false);
  const [plans, setPlans] = useState([]);
  const [packs, setPacks] = useState([]);
  const [plansLoading, setPlansLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [processingPack, setProcessingPack] = useState(null);

  useEffect(() => {
    Promise.all([getBillingPlans(), getTopupPacks()])
      .then(([plansRes, packsRes]) => {
        if (plansRes?.data?.success) setPlans(plansRes.data.data || []);
        if (packsRes?.data?.success) setPacks(packsRes.data.data || []);
      })
      .finally(() => setPlansLoading(false));
  }, []);

  useEffect(() => {
    setRecentLoading(true);
    getBillingTransactions({ page: 1, pageSize: 10 })
      .then((res) => {
        if (res?.data?.success) {
          const data = res.data.data;
          const list = Array.isArray(data)
            ? data
            : data?.transactions || data?.rows || [];
          setRecent(list);
        }
      })
      .finally(() => setRecentLoading(false));
  }, [activeBalance]);

  const handleAskConfirm = (plan) => setConfirmPlan(plan);

  const handleConfirmSubscribe = async () => {
    if (!confirmPlan) return;
    setProcessingPlan(confirmPlan.code);
    await subscribe(confirmPlan.code, confirmPlan.name, {
      onSuccess: () => {
        refreshBillingBalance();
        setConfirmPlan(null);
      },
      onDismiss: () => setProcessingPlan(null),
    });
    setProcessingPlan(null);
  };

  const openCancelCurrent = () => {
    setCancelMode("current");
    setShowCancel(true);
  };

  const openCancelUpgrade = () => {
    setCancelMode("upgrade");
    setShowCancel(true);
  };

  const handleBuy = async (pack) => {
    setProcessingPack(pack.code);
    await buyTopupPack(pack.code, {
      onSuccess: () => refreshBillingBalance(),
      onDismiss: () => setProcessingPack(null),
    });
    setProcessingPack(null);
  };

  const subscription = activeBalance?.subscription;
  const wallet = activeBalance?.wallet;
  const totalAvailable = activeBalance?.totalAvailable ?? 0;
  const walletBalance = wallet?.balance ?? 0;
  const scheduledNextPlan = activeBalance?.scheduledNextPlan;
  const trial = activeBalance?.trial;
  const trialAvailable = trial?.available === true;
  const subscriptionHasBalance =
    subscription?.status === "active" && (subscription?.remaining ?? 0) > 0;
  const showTopup = !subscriptionHasBalance;

  const usedPct =
    subscription && subscription.quota > 0
      ? Math.min(100, (subscription.used / subscription.quota) * 100)
      : 0;

  const renewsOn = subscription?.currentPeriodEnd
    ? moment(subscription.currentPeriodEnd).format("MMM D, YYYY")
    : null;

  const scheduledStartOn = scheduledNextPlan?.scheduledStartAt
    ? moment(scheduledNextPlan.scheduledStartAt).format("MMM D, YYYY")
    : null;

  const sortedPlans = [...plans].sort(
    (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
  );
  const sortedPacks = [...packs].sort(
    (a, b) => (a.displayOrder || 0) - (b.displayOrder || 0)
  );

  return (
    <>
      <div className="page-header-sticky">
        <h3 className="fw-bold">Billing & AI photoshoots</h3>
        <p>Manage your subscription and top-up credits.</p>
      </div>

      <div className="billing-dashboard">
        {trialAvailable && <TrialClaimBanner trial={trial} />}

      <div className="billing-hero-card">
        <div className="billing-hero-card-top">
          <div className="billing-hero-card-plan">
            {subscription ? (
              <>
                <span className="billing-hero-plan-name">
                  {subscription.planName} plan
                </span>
                {scheduledNextPlan && (
                  <>
                    <HiOutlineArrowsRightLeft className="billing-hero-plan-arrow" />
                    <span className="billing-hero-plan-name is-next">
                      {scheduledNextPlan.planName} plan
                      {scheduledStartOn && (
                        <span className="billing-hero-plan-name-date">
                          {" · "}
                          {scheduledStartOn}
                        </span>
                      )}
                    </span>
                  </>
                )}
                <span className="billing-hero-plan-status">Active</span>
                {subscription.cancelAtPeriodEnd && renewsOn && (
                  <span className="billing-hero-plan-cancel">
                    Ends {renewsOn}
                  </span>
                )}
              </>
            ) : (
              <span className="billing-hero-plan-name is-empty">
                No active plan
              </span>
            )}
          </div>
          <div className="billing-hero-card-actions">
            <button
              type="button"
              className="billing-hero-btn"
              onClick={() => scrollTo(plansRef)}
            >
              {subscription ? "Manage plan" : "Choose a plan"}
            </button>
            {showTopup && (
              <button
                type="button"
                className="billing-hero-btn"
                onClick={() => scrollTo(topupRef)}
              >
                {subscription ? "Add top-up" : "Buy credits"}
              </button>
            )}
            {subscription && !subscription.cancelAtPeriodEnd && !scheduledNextPlan && (
              <button
                type="button"
                className="billing-hero-btn"
                onClick={openCancelCurrent}
              >
                Cancel plan
              </button>
            )}
          </div>
        </div>

        <div className="billing-hero-card-count">
          <div className="billing-hero-number">{totalAvailable}</div>
          <div className="billing-hero-label">credits available</div>
        </div>

        <div className="billing-hero-card-bottom">
          {subscription ? (
            <>
              <div className="billing-hero-progress">
                <div
                  className="billing-hero-progress-fill"
                  style={{ width: `${usedPct}%` }}
                />
              </div>
              <div className="billing-hero-meta">
                <span>
                  <FiTrendingUp />
                  {subscription.used ?? 0} of {subscription.quota ?? 0} used
                  this month
                </span>
                {renewsOn && (
                  <span>
                    <FiCalendar />
                    {subscription.cancelAtPeriodEnd
                      ? `Ends on ${renewsOn}`
                      : `Renews on ${renewsOn}`}
                  </span>
                )}
              </div>
            </>
          ) : (
            <div className="billing-hero-meta billing-hero-meta-empty">
              <span>
                <FiTrendingUp />
                {walletBalance > 0
                  ? `${walletBalance} top-up credit${walletBalance === 1 ? "" : "s"} in your wallet`
                  : "Subscribe to a plan for monthly credits — or buy top-ups anytime."}
              </span>
            </div>
          )}
        </div>
      </div>

      <div className="billing-stats-grid">
        <div className="billing-stat-card">
          <div className="billing-stat-head">
            <div className="billing-stat-icon">
              <FiZap />
            </div>
            <div className="billing-stat-label">Top-up wallet</div>
          </div>
          <div className="billing-stat-value">
            {walletBalance}
            <span className="billing-stat-unit">
              credit{walletBalance === 1 ? "" : "s"}
            </span>
          </div>
          <div className="billing-stat-desc">
            Never expires. Activates once your monthly quota is used.
          </div>
        </div>

        {subscription ? (
          <div className="billing-stat-card">
            <div className="billing-stat-head">
              <div className="billing-stat-icon">
                <FaWandMagicSparkles />
              </div>
              <div className="billing-stat-label">This month</div>
            </div>
            <div className="billing-stat-value">
              {subscription.used ?? 0}
              <span className="billing-stat-unit">
                of {subscription.quota ?? 0} used
              </span>
            </div>
            <div className="billing-stat-desc">
              {subscription.remaining ?? 0} credits remaining in your{" "}
              {subscription.planName} plan
              {renewsOn && !subscription.cancelAtPeriodEnd && (
                <>. Resets on {renewsOn}.</>
              )}
            </div>
          </div>
        ) : (
          <div className="billing-stat-card">
            <div className="billing-stat-head">
              <div className="billing-stat-icon">
                <FaWandMagicSparkles />
              </div>
              <div className="billing-stat-label">Monthly plan</div>
            </div>
            <div className="billing-stat-value">
              0
              <span className="billing-stat-unit">no plan yet</span>
            </div>
            <div className="billing-stat-desc">
              Subscribe to a plan below to get monthly credits at a lower
              per-listing cost.
            </div>
          </div>
        )}
      </div>

      <div className="billing-section-block" ref={plansRef}>
        <div className="billing-section-block-head">
          <h5>{subscription ? "Change your plan" : "Choose a plan"}</h5>
          <p>Monthly quota, resets every billing cycle.</p>
        </div>
        {plansLoading && !plans.length ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <div className="billing-plans-grid">
            {sortedPlans.map((plan) => (
              <PlanCard
                key={plan.code}
                plan={plan}
                isCurrent={subscription?.planCode === plan.code}
                isScheduled={scheduledNextPlan?.planCode === plan.code}
                scheduledStartDate={scheduledStartOn}
                isProcessing={processingPlan === plan.code}
                onSubscribe={handleAskConfirm}
                onCancelUpgrade={openCancelUpgrade}
                hidePopular={!!subscription}
              />
            ))}
          </div>
        )}
      </div>

      {showTopup && (
        <div className="billing-section-block" ref={topupRef}>
          <div className="billing-section-block-head">
            <h5>
              <FiZap /> Or refill instantly
            </h5>
            <p>
              Top-up credits never expire and activate after your monthly
              quota is used.
            </p>
          </div>
          {plansLoading && !packs.length ? (
            <div className="text-center py-4">
              <Spinner animation="border" size="sm" />
            </div>
          ) : (
            <div className="billing-topup-grid">
              {sortedPacks.map((pack) => (
                <TopupCard
                  key={pack.code}
                  pack={pack}
                  isProcessing={processingPack === pack.code}
                  onBuy={handleBuy}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div className="billing-activity-card">
        <div className="billing-activity-header">
          <h5>Recent activity</h5>
          <Link to="/billing/history" className="billing-activity-link">
            View all →
          </Link>
        </div>
        <TransactionTable rows={recent} loading={recentLoading} />
      </div>

      <CancelSubscriptionModal
        isOpen={showCancel}
        toggle={() => setShowCancel(false)}
        subscription={subscription}
        scheduledNextPlan={scheduledNextPlan}
        mode={cancelMode}
      />

      <PlanSwitchConfirmModal
        isOpen={!!confirmPlan}
        toggle={() => setConfirmPlan(null)}
        targetPlan={confirmPlan}
        currentSubscription={subscription}
        scheduledNextPlan={scheduledNextPlan}
        onConfirm={handleConfirmSubscribe}
        isProcessing={!!processingPlan}
      />
      </div>
    </>
  );
};

export default DashboardView;
